import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Badge } from 'shards-react';
import {
  Table,
  THead,
  Th,
  Td,
  TableRow,
  TableHeader,
  TableWrapper,
  H3
} from '@increase/typed-components';
import Translator from '../../services/Translator';
import { NewTabLink } from './NewTabLink';

const STATUS_THEME = {
  ok: 'success',
  shared_with_other_credential: 'danger',
  shared_with_other_establishments: 'warning'
};

const ScrollableTable = styled.div`
  max-height: 280px;
  overflow-y: auto;

  thead {
    border-bottom: none;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: white;
    box-shadow: inset 0 -3px 0 ${({ theme }) => theme.colorsNew.gray.black};
  }
`;

export function ReadinessReport({ readiness }) {
  if (!readiness) return null;

  return (
    <div>
      <p>
        <strong>Credencial:</strong> {readiness.username} (
        {Translator.infoProvider(readiness.infoProvider)}) -{' '}
        {Translator.credentialType(readiness.credentialType)}
      </p>
      <p>
        <strong>Cuenta actual:</strong> {readiness.fromAccountName || readiness.fromAccountId}
      </p>
      {readiness.toAccountId && (
        <p>
          <strong>Cuenta destino:</strong> {readiness.toAccountName || readiness.toAccountId}
        </p>
      )}
      {readiness.blockers.length > 0 && (
        <div className="MarginBottom20">
          <strong>Bloqueos:</strong>
          <ul>
            {readiness.blockers.map((blocker, index) => (
              <li key={index} style={{ color: 'red' }}>
                {Translator.migrationBlocker(blocker)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p>
        <strong>¿Lista para migrar?</strong>{' '}
        <Badge pill theme={readiness.readyToMigrate ? 'success' : 'danger'}>
          {readiness.readyToMigrate ? 'Sí' : 'No'}
        </Badge>
      </p>
      <TableWrapper className="MarginTop20">
        <TableHeader>
          <H3>Establecimientos</H3>
        </TableHeader>
        <ScrollableTable>
          <Table hasData={readiness.establishments.length > 0} noDataMessage="No hay datos" zebra>
            <THead>
              <tr>
                <Th>Número</Th>
                <Th>Estado</Th>
                <Th>Detalle</Th>
              </tr>
            </THead>
            <tbody>
              {readiness.establishments.map((establishment) => (
                <TableRow key={establishment.id}>
                  <Td>
                    <NewTabLink to={`/admin/establishments/${establishment.id}`}>
                      {establishment.number}
                    </NewTabLink>
                  </Td>
                  <Td>
                    <Badge pill theme={STATUS_THEME[establishment.status] || 'secondary'}>
                      {Translator.migrationReadinessStatus(establishment.status)}
                    </Badge>
                  </Td>
                  <Td>
                    {establishment.conflictingCredential
                      ? `Compartido con ${establishment.conflictingCredential.username} ` +
                        `(${Translator.infoProvider(establishment.conflictingCredential.infoProvider)})`
                      : '-'}
                  </Td>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </ScrollableTable>
      </TableWrapper>
      <TableWrapper className="MarginTop20">
        <TableHeader>
          <H3>Shops</H3>
        </TableHeader>
        <ScrollableTable>
          <Table hasData={readiness.shops.length > 0} noDataMessage="No hay datos" zebra>
            <THead>
              <tr>
                <Th>Nombre</Th>
                <Th>Estado</Th>
                <Th>Detalle</Th>
              </tr>
            </THead>
            <tbody>
              {readiness.shops.map((shop) => (
                <TableRow key={shop.id}>
                  <Td>
                    {readiness.fromAccountId ? (
                      <NewTabLink
                        to={`/admin/accounts/${readiness.fromAccountId}/shops/${shop.id}`}
                      >
                        {shop.name}
                      </NewTabLink>
                    ) : (
                      shop.name
                    )}
                  </Td>
                  <Td>
                    <Badge pill theme={STATUS_THEME[shop.status] || 'secondary'}>
                      {Translator.migrationReadinessStatus(shop.status)}
                    </Badge>
                  </Td>
                  <Td>
                    {shop.status === 'shared_with_other_establishments'
                      ? `Compartido con: ${shop.conflictingEstablishments
                          .map((est) => est.number)
                          .join(', ')}. ${
                          shop.equivalentShopExists === null
                            ? 'Sólo se mueven los establecimientos que migran.'
                            : shop.equivalentShopExists
                              ? 'Ya existe un shop equivalente en la cuenta destino.'
                              : 'Se creará un shop equivalente en la cuenta destino.'
                        }`
                      : '-'}
                  </Td>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </ScrollableTable>
      </TableWrapper>
    </div>
  );
}

ReadinessReport.propTypes = {
  readiness: PropTypes.shape({
    username: PropTypes.string,
    infoProvider: PropTypes.string,
    credentialType: PropTypes.string,
    fromAccountId: PropTypes.string,
    fromAccountName: PropTypes.string,
    toAccountId: PropTypes.string,
    toAccountName: PropTypes.string,
    blockers: PropTypes.arrayOf(PropTypes.string),
    readyToMigrate: PropTypes.bool,
    establishments: PropTypes.array,
    shops: PropTypes.array
  })
};
